import React from "react";
import { MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel } from "@material-ui/core";
import DraftNumberInput from "../../../inputs/DraftNumberInput";
import ProductWidgetContainer from "./ProductWidgetContainer";
import styled from "styled-components/macro";

const FlexSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const InfoBox = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 10px 14px;
  font-size: 0.875rem;
  color: #664d03;
  margin-top: 4px;
`;


const DocumentUnderstandingWidget = ({
  product,
  onRemove,
  onProductChange,
  editable,
  baselineProfile,
  deploymentType,
}: any) => {
  const handleVersionChange = (e: React.ChangeEvent<any>) => {
        const {value} = e.target;
        const productCopy: any = {...product};
        productCopy.properties["modelVersion"] = value;
        onProductChange(productCopy);
    };

  const handleProfileChange = (newProfile: string) => {
    const productCopy = { ...product, profile: newProfile };
    onProductChange(productCopy);
  };

  const handleBooleanChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (product?.properties && name) {
      const productCopy = { ...product };
      productCopy.properties = { ...productCopy.properties };
      productCopy.properties[name] = checked;
      onProductChange(productCopy);
    }
  };

  const numberInput = (label: string, field: string) => (
    <DraftNumberInput
      label={label}
      product={product}
      field={field}
      baselineProfile={baselineProfile}
      editable={editable}
      onProductChange={onProductChange}
    />
  );

  const isModernProjectsOnly = product.key === "document_understanding_modern_projects";
  const isAiCenterOnly = product.key === "document_understanding_ai_center";
  const cpuInference = !!product.properties?.cpuInference;
  const extendedOcr = !!product.properties?.extendedOcr;

  return (
    <ProductWidgetContainer
      onRemove={onRemove}
      title={product.name}
      productKey={product.key}
      canBeRemoved={product.canBeRemoved}
      productProfile={product.profile}
      deploymentType={deploymentType}
      onProfileChange={handleProfileChange}
    >
      {!isModernProjectsOnly && <>
        <FlexSection>
          <div>
            <InputLabel id="model-version-label">Model Version</InputLabel>
            <FormControl fullWidth>
              <Select
                label="Model Version"
                labelId="model-version-label"
                value={product.properties?.modelVersion || "22.10"}
                onChange={handleVersionChange}
                disabled={!editable}
              >
                <MenuItem key={"22.10"} value={"22.10"}>22.10</MenuItem>
                <MenuItem key={"23.4"} value={"23.4"}>23.4</MenuItem>
                <MenuItem key={"23.7"} value={"23.7"}>23.7</MenuItem>
                <MenuItem key={"23.10"} value={"23.10"}>23.10</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FlexSection>

        <FlexSection>
          {numberInput("General Extractor (pages per hour)", "generalExtractorPerHour")}
          {numberInput("Invoices (pages per hour)", "invoicesPerHour")}
          {numberInput("Receipts (pages per hour)", "receiptsPerHour")}
          {numberInput("Purchase Orders (pages per hour)", "purchaseOrdersPerHour")}
          {numberInput("Utility Bills (pages per hour)", "utilityBillsPerHour")}
          {numberInput("OCR (pages per hour)", "ocrPerHour")}
          {numberInput("Intelligent Form Extractor (handwriting shreds per hour)", "shredsPerHour")}
        </FlexSection>
      </>}

      {!isAiCenterOnly && <>
        <FlexSection>
          {numberInput("Custom Model Pages per Hour", "customModelPagesPerHour")}
          {numberInput("OOB Model Pages per Hour", "oobModelPagesPerHour")}
          {numberInput("Models Training in Parallel", "modelsTrainingInParallel")}
        </FlexSection>
        <FlexSection>
          <FormControlLabel
            control={
              <Switch
                checked={cpuInference}
                onChange={handleBooleanChange}
                name="cpuInference"
                disabled={!editable}
                color="primary"
              />
            }
            label="Enable CPU Inference"
          />
        </FlexSection>
        {cpuInference && (
          <InfoBox>
            OOB model and Custom model run on CPU instead of GPU nodes.
            See <a href="https://docs.uipath.com/automation-suite/docs/document-understanding-hardware-requirements" target="_blank" rel="noopener noreferrer">CPU inference limitations</a> for details.
          </InfoBox>
        )}
        <FlexSection>
          <FormControlLabel
            control={
              <Switch
                checked={extendedOcr}
                onChange={handleBooleanChange}
                name="extendedOcr"
                disabled={!editable}
                color="primary"
              />
            }
            label="Enable Extended Languages OCR"
          />
        </FlexSection>
        {extendedOcr && (
          <InfoBox>
            Extended Languages OCR enables recognition for over 200 languages, including Chinese, Korean, Vietnamese, Thai, Indian languages, and those using Cyrillic or Greek alphabets.
            See <a href="https://docs.uipath.com/automation-suite/automation-suite/2024.10/installation-guide-openshift/enable-or-disable-extended-languages-ocr" target="_blank" rel="noopener noreferrer">Enable or Disable Extended Languages OCR</a> for details.
          </InfoBox>
        )}
      </>}

    </ProductWidgetContainer>
  );
};

export default DocumentUnderstandingWidget;
